<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <div class="border rounded-lg bg-white p-2 w-100">
            <div v-if="hasChartError && !showConfig" key="chartErrorArea" class="py-4 text-center">
                <h6>Invalid Chart Configuration</h6>
                <p class="text-muted small">({{ invalidChartMsg }})</p>
            </div>
            <div v-if="showConfig" key="configArea" class="mx-4 my-3">
                <b-form-group
                    label="Chart Type"
                    label-class="font-weight-bold"
                    label-align="right"
                    description="The type of chart to use"
                    label-cols="3"
                >
                    <b-form-select 
                        v-model="editConfigValues.localChartKey" 
                        :options="chartConfigOptions"
                        text-field="label"
                        value-field="key"
                    ></b-form-select>
                </b-form-group>
                <b-form-group
                    label="Series Categories"
                    label-class="font-weight-bold"
                    label-align="right"
                    description="The field to use to split into series"
                    label-cols="3"
                >
                    <b-form-select 
                        v-model="editConfigValues.splitKey" 
                        :options="possibleSeriesOptions"
                        text-field="label"
                        value-field="value"
                    ></b-form-select>
                </b-form-group>
                <b-form-group
                    v-if="showConfigXAxisSelect"
                    label="X-Axis Categories"
                    label-class="font-weight-bold"
                    label-align="right"
                    description="The categories to use on the x-axis of the chart"
                    label-cols="3"
                >
                    <b-form-select 
                        v-model="editConfigValues.localXKey" 
                        :options="possibleXAxisOptions"
                        text-field="label"
                        value-field="value"
                    >
                        <b-form-select-option value="">--None-- (show series across x-axis)</b-form-select-option>
                    </b-form-select>
                </b-form-group>
                <div class="d-flex gap-2 align-items-center justify-content-end">
                    <b-button variant="minimal-secondary" @click="cancelConfigMode">Cancel</b-button>
                    <b-button variant="primary" @click="applyConfigChanges">Update</b-button>
                    <!-- had v-show="chartLoaded" on both buttons... not sure why -->
                </div>
            </div>
            <div v-else-if="showChart" key="chartArea" class="">
                <!-- set height on the Chart element itself, then set responsive: true, maintainAspectRatio: false in chartOptions -->
                <Chart 
                    ref="chartComponent"
                    :type="chartConfig.chartType" 
                    :data="chartData" 
                    :options="chartOptions" 
                    :style="{
                        height: chartHeight,
                    }"
                    @select="handleChartSelect"
                    @loaded="handleChartLoaded"
                />
            </div>
            <div v-if="!showConfig" class="mt-1 d-flex gap-3 align-items-center justify-content-end">
                <b-link 
                    @click="downloadChartImage" 
                    class="text-uppercase font-weight-700 font-size-70 text-decoration-none"
                    :disabled="hasChartError"
                >
                    <b-icon icon="cloud-arrow-down" class=""></b-icon>
                    Download
                </b-link>
                <b-link 
                    @click="resetConfig" 
                    class="text-uppercase font-weight-700 font-size-70 text-decoration-none"
                >
                    <b-icon icon="arrow-counterclockwise" class=""></b-icon>
                    Reset
                </b-link>
                <b-link 
                    @click="enterConfigMode" 
                    class="text-uppercase font-weight-700 font-size-70 text-decoration-none"
                >
                    <b-icon icon="gear" class=""></b-icon>
                    Configure
                </b-link>
            </div>
        </div>
    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import Chart from 'primevue/chart';
import groupBy from 'lodash/groupBy';
import countBy from 'lodash/countBy';
import set from 'lodash/set';
import get from 'lodash/get';

const colorListOne = [  // bright pastels
    'rgba(255, 99, 132, 1)',
    'rgba(255, 159, 64, 1)',
    'rgba(255, 205, 86, 1)',
    'rgba(75, 192, 192, 1)',
    'rgba(54, 162, 235, 1)',
    'rgba(153, 102, 255, 1)',
    'rgba(201, 203, 207, 1)',
];

const colorListTwo = [  // meh...
    '#4dc9f6',
    '#f67019',
    '#f53794',
    '#537bc4',
    '#acc236',
    '#166a8f',
    '#00a950',
    '#58595b',
    '#8549ba'
];

const allColorLists = [colorListOne, colorListTwo];

const getColor = (index, colorListIndex) => {
    const colorListsLength = allColorLists.length;
    const selectedListIndex = colorListIndex ?? 0;
    const clist = (selectedListIndex < colorListsLength) ? allColorLists[selectedListIndex] : allColorLists[0];
    const numColors = clist.length;
    const colorIndex = index % numColors;
    return clist[colorIndex];
};

const singleBackgroundColor = 'rgba(54, 162, 235, 1)';


// DEFAULTS FOR HANDLING VALUES THAT ARE undefined / NOT SET
const undefinedPlaceholderValue = '__UNDEFINED__';
const undefinedPlaceholderLabel = '(Not Set)';
const nullPlaceholderValue = '__NULL__';  // use same for now
const nullPlaceholderLabel = undefinedPlaceholderLabel;  // use same for now

const getItemValueOrPlaceholder = (item, key) => {
    // helper to use throughout to get some key from an item
    // if value is specifically `undefined` OR `null`, we will return the undefinedPlaceholderValue
    // any 'unset' FK field will have null
    // any 'unset' nested data field (in metadata) will return undefined
    // inside lodash get() - the 3rd arg is only for dealing with undefined things -- so we add ?? to handle things that return null specifically
    // NEW July 7 2022 - also coalesce '' empty string to undefined
    const returnValueMap = {
        undefined: undefinedPlaceholderValue,
        null: undefinedPlaceholderValue,
        '': undefinedPlaceholderValue,
    }
    // WORKS FOR DEALING WITH undefined or null:  return get(item, key, undefinedPlaceholderValue) ?? nullPlaceholderValue;
    const rawVal = get(item, key);  // might return undefined, null, etc... pass this to returnValueMap -- if the value isn't in there, then its a valid value, and return it
    // NEW Dec 15 2022 - handle the value of key in each item being a nested object (to deal with rank_obj / severity_obj)
    //      for now only handles nested object with specific 'value' in them -- ideally you should pass this down from parent/backend in 'fieldList' 
    if (rawVal in returnValueMap) {
        return returnValueMap[rawVal];
    } else if (rawVal?.value) {
        // if rawVal is a nested object WITH 'value' key, return that
        return rawVal.value;
    } else {
        return rawVal;
    }
    //return returnValueMap[val] ?? val;
}



class ChartSeries {
    constructor({
        key,  // the value of the seriesKey for this series
        label,  // human friendly label
        fullLabel,  // human friendly label + seriesKeyName
        items,  // Array of items in this series
        //data,  // single datapoint if there are no bins, OR array of datapoints if there are multiple bins
        backgroundColor,  // the color we set, as a string
        fill,  // for area charts -- value of fill can be many things - you'll likely use 'stack' or 'origin'
    }) {
        this.key = key;
        this.label = label;
        this.fullLabel = fullLabel || label;
        this.items = items;
        //this.data = data || null;
        this.backgroundColor = backgroundColor;
        this.fill = fill;
    }
    getCounts(field, valuesArray) {
        // 'field' - the name of a field on each item
        // 'valuesArray' - array of "bins" or null -- if array, return array of same length with the count of items with that value in field
        // if you pass in array for valuesArray, you will get array of data back for each bin -- same length array
        // if you pass in no bins (falsey valuesArray), you will get a single value back
        const fieldGetter = (item) => {
            // use this in countBy to include our special 'Not Set' things
            return getItemValueOrPlaceholder(item, field);
        };
        if (!field || !valuesArray || !Array.isArray(valuesArray) || (valuesArray.length < 1)) {
            // if no valuesArray (bins), or empty array (empty array is likely), then just return total count of items!
            // also, if we did not provide 'field' then we don't know what to check on each item - just return total item count
            return this.items.length;
        } else {
            //const itemBinProperty = bins[0].itemBinProperty;  // a string like 'monthBin' -- so we keep it defined in only one place
            //const itemCountsByBin = countBy(this.items, field);  // returns object, but keys are STRINGS here, no longer numbers
            const itemCountsByBin = countBy(this.items, fieldGetter);  // returns object, keys are STRINGS here, and we will have our special placeholders for null/undefined included
            //console.log('from getCounts countBy', itemCountsByBin);
            // now run back through valuesArray to keep output in same order, get the counts for each value from the result of countBy
            const countsArray = valuesArray.map(val => {
                const valString = String(val);  // coerce things to strings here, because in itemCountsByBin above, they will be strings
                return itemCountsByBin[valString] ?? 0;  // if no counts for that bin, default to zero
            });
            return countsArray;  // will be an array, same length as valuesArray
        }
    }
    getItemCountsByValue(field) {
        // build & return object mapping { valueOfField: countsForThatField }
        // WILL ONLY INCLUDE keys for things where you actually have items/count! not all possible values
        const fieldGetter = (item) => {
            // use this in countBy to include our special 'Not Set' things
            return getItemValueOrPlaceholder(item, field);
        };
        if (!field) {
            // also, if we did not provide 'field' then we don't know what to check on each item - just return total item count
            return this.items.length;
        } else {
            const itemCountsByBin = countBy(this.items, fieldGetter);  // returns object, keys are STRINGS here, and we will have our special placeholders for null/undefined included
            //console.log('from getItemCountsByValue countBy', itemCountsByBin);
            return itemCountsByBin;
        }
    }
    makeDatasetWithData(dataArray) {
        // pass in a data array, likely originally made from getItemCountsByValue, 
        // put this data in with other stuff and return it as expected for a 'dataset' for ChartJS
        return {
            key: this.key,
            label: this.label,
            fullLabel: this.fullLabel,
            data: dataArray,
            backgroundColor: this.backgroundColor,
            fill: this.fill,
        }
    }
    makeDataset(field, valuesArray) {
        // datasets have { label, fullLabel (our own), data, borderColor, backgroundColor, fill, hidden, ... }
        return {
            key: this.key,
            label: this.label,
            fullLabel: this.fullLabel,
            data: this.getCounts(field, valuesArray),
            backgroundColor: this.backgroundColor,
            fill: this.fill,
        }
    }
}


export default {
    name: 'AnalyticsChart',
    components: {
        Chart,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        items: {  // the FILTERED items from parent
            type: Array,
            required: true,
        },
        fieldList: {  // array of all fields (with options, options_map, etc.) from parent
            // we will pull things from here based on the seriesKey
            // MUST BE REACTIVE - because our dateBins are in here
            type: Array,
            required: true,
        },
        xKey: {  // field to use to slice/bin things on X axis - for date bins, use 'dateBins.year', etc.
            type: String,
            required: false,
        },
        xAxisSkipEmpty: {
            // should we not show bins/values in x-axis that are empty?  default false, which makes sense when showing date bins
            // TODO - figure out how to implement this!
            type: Boolean,
            default: false,
        },
        seriesKey: {  // the 'splitKey' - but we'll call it seriesKey in props - the Y-axis essentially
            // NOT required -- if showing totals by bins, then we won't have it, for example
            type: String,
            default: '',  // keep emptry string as default... we will set it on this.splitKey 
        },
        /* series: {  // REMOVED THESE - MAYBE ADD AGAIN LATER - unnecessary complexity at this point
            // OPTIONAL OBJECT with { val1: 'Label One', val2: "Label Two" } for specific series to include
            // if you use this, ONLY these series will be included... if not used, then it uses all series it finds based on seriesKey
            // include { 'undefined': 'NOT SET' } or something similar if you want to include empty / undefined values
            type: Object,
            required: false,  // do not set default... because we might want to pull from fieldList and we expect this to be undefined in that case
        },
        seriesKeyName: {
            // the friendly name of the splitKey / seriesKey - used for display things only
            // ie: seriesKey = 'metadata.rank' // seriesKeyName = 'Issue Rank'
            // if not provided, we try to get it from fieldList
            type: String,
            required: false,  // do not set default... because we might want to pull from fieldList and we expect this to be undefined in that case
        }, */
        chartKey: {  // OUR OWN CUSTOM TYPES - not same as Chart component type - used to set chartConfigObj
            type: String,
            default: 'bar',
        },
        height: {  // Number or string like '100%' etc.
            type: [Number, String],
            default: 400,
        },
        title: {
            type: String,
            required: false,
        },
        yAxisTitle: {
            type: String,
            required: false,
        },
        itemName: {
            // the name of the items we are working with
            type: String,
            required: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            invalidChartMsg: '',  // set to some non-empty string if something is not valid, cannot build chart -- will show error message instead
            mode: 'chart', // other options: 'config', 'error'?  or just look for some invalidChartMsg?
            chartInstance: null,  // the ChartJS instance, emitted from Chart component when it first @loaded -- our event handler for that event will stash here
            chartData: null,  // set it from method
            chartOptions: null,  // set in from method
            // datapointDefs - we set this as we are setting chartData
            // it holds objects that define 'x-axis' of the chart, either the bins if using bins, or the series if one bin, mutliple series (pie charts)
            // we need it to lookup what we clicked on -- its the 'index' in select event
            datapointDefs: [],  
            // splitKey, splitKeyName, splitKeyLabels -- these are the 'local' data for seriesKey, seriesKeyName, series
            // set them in created based on what props you have, or get from fieldList
            splitKey: '',
            splitKeyName: '',
            splitKeyLabels: {},
            seriesInstances: [],  // Array of ChartSeries class instances
            localXKey: '',  // set in created, then update if changing config
            editConfigValues: {}, // copy values here when you ENTER config mode, then IF YOU APPLY THEM, copy them to the real data things
            localChartKey: '',  // set from this.chartKey prop in created/mounted, v-model to 'key' in chartConfigOptions below
            chartConfig: {},  // the SELECTION option object from the options below, which we set on first created/mounted, AND if we change config
            chartConfigOptions: [
                {
                    key: 'bar',  // key is the 'value' we use in select input, our own unique thing
                    chartType: 'bar',  // the actual type we pass to Chart component
                    isStacked: false,
                    label: 'Bar Chart',
                    hasXAxis: true,
                },
                {
                    key: 'stacked_bar',
                    chartType: 'bar',  // the actual type we pass to Chart component
                    isStacked: true,
                    label: 'Stacked Bar Chart',
                    hasXAxis: true,
                },
                {
                    key: 'stacked_area',
                    chartType: 'line',
                    isStacked: true,
                    label: 'Stacked Area Chart',
                    hasXAxis: true,
                },
                {
                    key: 'line',
                    chartType: 'line',
                    isStacked: false,
                    label: 'Line Chart',
                    hasXAxis: true,
                },
                {
                    key: 'pie',
                    chartType: 'pie',
                    label: 'Pie Chart',
                    hasXAxis: false,
                },
                {
                    key: 'doughnut',
                    chartType: 'doughnut',
                    label: 'Donut Chart',
                    hasXAxis: false,
                },
            ]
        }
    },
    computed: {
        possibleSeriesOptions() {
            // return filtered list of this.fieldList, with only type: 'select' or 'boolean' for now -- can expand this later once you can split series based on text fields
            // ALSO - only use columns/fields that have 'options'/'optionsMap'
            // For boolean fields, add Yes/No options
            const selectOptions = this.fieldList.filter(fld => ((fld.type === 'select') && !!fld.options && Array.isArray(fld.options) && fld.options.length)).map(fld => ({ value: fld.key, label: fld.label}));
            const booleanOptions = this.fieldList.filter(fld => fld.type === 'boolean').map(fld => ({ value: fld.key, label: fld.label}));
            return [...selectOptions, ...booleanOptions];
        },
        possibleXAxisOptions() {
            // same as possibleSeriesOptions - but remove the current splitKey selection - or just disable it???
            // ALSO - only use columns/fields that have 'options' key - you might have 'select' things without specified options, if options are dynamic on frontend
            const selectOptions = this.fieldList.filter(fld => ((fld.type === 'select') && !!fld.options && Array.isArray(fld.options) && fld.options.length)).map(fld => {
                return { value: fld.key, label: fld.label, disabled: (fld.value === this.editConfigValues.splitKey)};
            });
            // For boolean fields, add Yes/No options
            const booleanOptions = this.fieldList.filter(fld => fld.type === 'boolean').map(fld => ({ value: fld.key, label: fld.label, disabled: false }));
            return [...selectOptions, ...booleanOptions];
        },
        showConfigXAxisSelect() {
            // if current localChartKey hasXAxis=true, then show it while editing config - must get from the things we're v-model with while editing!
            const currentChartConfigObj = this.chartConfigOptions.find(opt => (opt.key === this.editConfigValues.localChartKey));
            return currentChartConfigObj ? currentChartConfigObj.hasXAxis : false;
        },
        hasXAxis() {
            // is there an x axis for this chart?  must be correct type, and have localXKey
            //const xAxisTypes = ['bar', 'line'];
            // NOW JUST GETTING FROM FIELD IN chartConfig
            return this.chartConfig.hasXAxis && !!this.localXKey;
        },
        xAxisFieldDef() {
            // the 'field' object used in xAxis, if any - return null if there is no X-axis based on chart type or props
            if (!this.hasXAxis) {
                return null;
            }
            const xFieldDef = this.fieldList.find(fldObj => (fldObj.key === this.localXKey));  // returns undefined if not found
            return xFieldDef;
        },
        // NOTE - for xAxis stuff, if you are only pulling from KNOWN values, anything with null/undefined will NOT get included....
        //      - might need to add a group for 'Not Set' -- and make sure any null/undefined values in the series/dataset are replaced with undefinedPlaceholderValue
        /* xAxisObjects() {  // Array of objects with { value, label } for whatever we're putting on x-axis
            const xFieldDef = this.xAxisFieldDef;  // this.fieldList.find(fldObj => (fldObj.key === this.localXKey));  // returns undefined if not found
            if (!xFieldDef) {
                return [];
            }
            return xFieldDef.options.map(opt => ({ ...opt, fullLabel: `${xFieldDef.label}: ${opt.label}` }));  // add fullLabel so you can display it in title of clicked selections
        },
        xAxisValues() {
            // the 'values' of the field we're showing in X-axis (if any) - not the labels to display, but used to get things from items
            return this.xAxisObjects.map(xo => xo.value);
        },
        xAxisLabels() {
            // the labels to show on the x-axis, if any
            return this.xAxisObjects.map(xo => xo.label);
        }, */
        // SHORTCUTS TO THINGS IN CHART CONFIG -- make sure they are responsive in time, or wrap things in nextTick!
        isStacked() {
            return this.chartConfig.isStacked;
        },
        isFilled() {
            // for area charts -- they are type: 'line' with fill=true in datasets
            const filledChartKeys = ['stacked_area']; // any more?
            return filledChartKeys.includes(this.localChartKey);
        },
        isValidForChart() {  // MOVE TO CREATED - and use invalidChartMsg
            // do we have enough stuff to defined a chart?  does not mean we're ready to show it, but that we will eventually be ready to show it
            // we must have either a splitKey, or bins, or both... and items
            // if no splitKey and no bins, then its just a single count of all items.... show a gauge?
            const hasItems = !this.items && Array.isArray(this.items) && this.items.length;
            return hasItems && (this.localXKey || !!this.splitKey);
            // TODO - update this?
        },
        hasChartError() {
            return !!this.invalidChartMsg;
        },
        showConfig() {
            // show the config inputs
            return this.mode === 'config';
        },
        showChart() {
            // is everything ready to show the chart?  should not have errors
            return (this.mode === 'chart') && !this.invalidChartMsg && !!this.chartData && !!this.chartConfig.chartType && !!this.chartOptions;
        },
        chartHeight() {
            return typeof this.height === 'number' ? `${this.height}px` : this.height;
        },
        chartLoaded() {
            return !!this.chartInstance;
        },
        finalTitle() {
            // if you don't have this.title in props, make one
            // fallback is 'Y-axis' by 'X-axis', or 'by Y-axis'
            if (this.title) {
                return this.title;
            }
            const xAxisLabel = this.xAxisFieldDef ? this.xAxisFieldDef.label : '';
            let description = '';
            if (this.splitKeyName && xAxisLabel) {
                description = `${this.splitKeyName} by ${xAxisLabel}`;
                return [this.itemName, description].filter(t => !!t).join(' - ');
            } else if (this.splitKeyName || xAxisLabel) {
                // if you only have one...
                description = `By ${this.splitKeyName || xAxisLabel}`;
                return [this.itemName, description].filter(t => !!t).join(' - ');
            }
            return '';
        },
        finalYAxisTitle() {
            const typesWithYAxis = ['bar', 'line'];
            if (!typesWithYAxis.includes(this.chartConfig.chartType)) {
                return '';
            }
            if (this.yAxisTitle) {
                return this.yAxisTitle;
            }
            return this.itemName;
        },
    },
    watch: {
        // don't need watchers on everything... just the stuff that will change with filters
        items: function() {
            this.updateData();
        },
        fieldList: function() {
            this.updateData();
        },
    },
    created() {
        //this.setLocalData();  // take props and set data based on them -- only do it in created/mounted, not when doing any update, in case you change config
        // set 'one-time' stuff from props to local data 
        this.resetConfig();
    },
    mounted() {
        //this.updateAll();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        enterConfigMode() {
            this.editConfigValues = {};  // start with blank object in case we have anything from before
            const configThings = ['localChartKey', 'localXKey', 'splitKey'];  // change below too, if you change this
            configThings.forEach(attr => {
                // we v-model with this.editConfigValues - must set these before you show the config stuff or else it will errror
                this.$set(this.editConfigValues, attr, this[attr]);  // copy things from top level data to nested config object
            });
            this.mode = 'config';
        },
        cancelConfigMode() {
            // you did not change any of the real data values, so just change modes, then blank the editConfigValues so its clean for next time
            this.mode = 'chart';
        },
        applyConfigChanges() {
            // set the local data to new things, rebuild chart data, then change modes, then reset editConfigValues
            const configThings = ['localChartKey', 'localXKey', 'splitKey'];
            console.log('applyConfigChanges');
            configThings.forEach(attr => {
                // we v-model with this.editConfigValues 
                console.log(`setting this.${attr} to:`, this.editConfigValues[attr]);
                this[attr] = this.editConfigValues[attr];  // copy things from editConfigValues (our v-models) back to top-level data
            });
            /* // must also set this.splitKeyName and this.splitKeyLabels from new value of this.splitKey
            const fieldDef = this.fieldList.find(fldObj => (fldObj.key === this.splitKey));  // returns undefined if not found
            // should have it since we just set splitKey based on fieldList...
            this.splitKeyName = fieldDef.label || '';  // just for display
            this.splitKeyLabels = fieldDef.options_map;   */
            this.setLocalConfig();  // sets all config stuff from the things you changed
            // give computed stuff a chance to update... wrap the rest in nextTick - we put it in updateAll() instead
            this.updateAll();  // update all the chart data
            this.mode = 'chart';
            //this.editConfigValues = {};  // reset it so its ready for next time
            // TODO LATER - EMIT CHANGES TO PARENT SO NEW DASHBOARD SETTINGS CAN BE SAVED
        },
        resetConfig() {
            // set / reset the local config stuff to original values from PROPS
            // also call this when first created/mounted to set props to local values
            this.localXKey = this.xKey;
            this.localChartKey = this.chartKey;
            this.splitKey = this.seriesKey;
            this.setLocalConfig();  // must set things from props to local values before calling this
            this.updateAll();
        },
        setLocalConfig() {
            // set config-related things from the main props and/or configured v-models -- call this on initial load, and after updating any config settings
            // all local things should be set from props if you have props for them
            // SET chartConfig STUFF FROM chartConfigOptions / localChartKey (set from chartKey on first load)
            const foundChartConfigObj = this.chartConfigOptions.find(opt => (opt.key === this.localChartKey));
            let hasError = false;  // track any errors... if no new ones, make sure to clear this.invalidChartMsg
            if (!foundChartConfigObj) {
                hasError = true;
                console.warn(`From AnalyticsChart, localChartKey is ${this.localChartKey} but we cannot find matching chartConfig object for it in chartConfigOptions - will hide`);
                this.invalidChartMsg = 'Invalid chart key';
                return;
            }
            this.chartConfig = Object.assign({}, foundChartConfigObj);
            // that sets the chartType, other stuff -- we'll pull directly from that
            // SET SPLIT KEY THINGS IF YOU HAVE SPLIT KEY
            if (this.splitKey) {  // do we have splitKey?  If yes, then set other series/split-related things in localData.  if not, move on
                const fieldDef = this.fieldList.find(fldObj => (fldObj.key === this.splitKey));  // returns undefined if not found
                if (!fieldDef) {
                    hasError = true;
                    console.warn(`From AnalyticsChart, splitKey is ${this.splitKey} but we cannot find matching field for it in fieldList - will hide`);
                    this.invalidChartMsg = 'Invalid split key';
                    return;
                }
                this.splitKeyName = fieldDef.label || '';  // just for display
                // Handle boolean fields: provide Yes/No options_map
                if (fieldDef.type === 'boolean') {
                    this.splitKeyLabels = { true: 'Yes', false: 'No' };
                    fieldDef.options = [
                        { value: true, label: 'Yes' },
                        { value: false, label: 'No' }
                    ];
                    fieldDef.options_map = { true: 'Yes', false: 'No' };
                } else {
                    this.splitKeyLabels = fieldDef.options_map;
                }
                if (!this.splitKeyLabels) {
                    // MAYBE THIS IS OK???? if you don't have a 'select' field, but a text field with few options, maybe you can still group by those options?  ok, later...
                    hasError = true;
                    console.warn(`From AnalyticsChart, splitKey is ${this.splitKey} but we cannot get the options_map or series for it - will hide`);
                    this.invalidChartMsg = 'Cannot find options for split key';
                    return;
                }
            } else {
                // we'll be explicit about setting values if we don't have seriesKey 
                this.splitKey = '';
                this.splitKeyName = '';
                this.splitKeyLabels = {};  // or do you want to put the single 'all' series in here?  
            }
            if (!hasError) {
                this.invalidChartMsg = '';  // clear this so the chart shows!
            }
        },
        buildSeries() {
            // start by clearing our stuff previously rendered / created - like this.seriesInstances
            this.seriesInstances = [];
            // ONLY work with local data here -- this.splitKey, this.splitKeyLabels, this.splitKeyName
            if (this.splitKey) {
                // MAKE THE SERIES
                const getSplitKeyValue = (item) => {
                    // this is a helper to help deal with 'undefined' 
                    // NEW March 17 2022 - also setting to our undefined placeholder for any 'null' values -- any FKs without a value will be null rather than 'undefined'
                    return getItemValueOrPlaceholder(item, this.splitKey);  // will return placeholder for 'undefined' or 'null'
                    //return get(item, this.splitKey, undefinedPlaceholderValue) ?? undefinedPlaceholderValue;  // added nullish coalescing operator to default to same thing for null values
                }
                const getSplitKeyLabel = (splitKeyValue) => {
                    if (splitKeyValue === undefinedPlaceholderValue) {
                        return undefinedPlaceholderLabel;
                    } else if (splitKeyValue === nullPlaceholderValue) {
                        return nullPlaceholderLabel;
                    } else {
                        return this.splitKeyLabels[splitKeyValue] ?? splitKeyValue ?? undefinedPlaceholderLabel;  // not sure if we will ever need all these last nullish coalescing things...
                    }
                };
                const splitGroups = groupBy(this.items, getSplitKeyValue);  // returns object with keys of the different possible values of splitKey, values is array of items
                Object.entries(splitGroups).forEach(([splitKeyValue, splitItems], index) => {
                    const splitKeyLabel = getSplitKeyLabel(splitKeyValue);
                    const series = new ChartSeries({
                        key: splitKeyValue,
                        label: splitKeyLabel,
                        fullLabel: `${this.splitKeyName}: ${splitKeyLabel}`,
                        items: splitItems,
                        backgroundColor: getColor(index),
                        fill: (this.isFilled && this.isStacked) ? 'stack' : '',
                    })
                    this.seriesInstances.push(series);
                });
            } else {
                // just make single series if no splitKey
                const singleSeries = new ChartSeries({
                    key: 'all',
                    label: 'All Items',
                    fullLabel: 'All Items',
                    items: this.items,
                    backgroundColor: singleBackgroundColor,
                    fill: (this.isFilled && this.isStacked) ? 'stack' : '',
                });
                this.seriesInstances.push(singleSeries);
            }
        },
        setChartDataFromSeries() {
            // now that you have this.seriesInstances, figure out format of data based on chart type, get it from series, and set it on this.chartData
            if (this.hasXAxis) {
                // do you have an x-axis to show?  then each series will have an array of data
                // this works if you have multiple series, or only one series
                // March 17 2022 - we used to have these xAxis___ things as computed, but we only use them here, so moving here
                const xFieldDef = this.xAxisFieldDef;  // from computed - returns undefined if not found
                const xAxisOptions = xFieldDef ? [...xFieldDef.options] : [];  // MUST MAKE COPY OF .options HERE -- this is shared by all chart instances on the page!!!  default to empty array here... options are from the field definition from backend
                // instead of making the data arrays in the series instances themselves, we'll get the counts for all values here, keyed by value { value: count }
                // then look to see if we have undefined/null, then make the data arrays here
                // NOTE - all keys in countMaps will be strings (I think?) - important when getting data from them below
                const datasetCountMaps = this.seriesInstances.map(ser => ser.getItemCountsByValue(this.localXKey));
                const hasNull = datasetCountMaps.some(cm => (nullPlaceholderValue in cm));
                const hasUndefined = datasetCountMaps.some(cm => (undefinedPlaceholderValue in cm));
                // if we have x-axis 'bins' with null or undefined, must add them to xAxisOptions
                if (hasNull) {
                    const nullOption = {
                        label: nullPlaceholderLabel,
                        value: nullPlaceholderValue,
                    };
                    xAxisOptions.push(nullOption);
                }
                if (hasUndefined) {
                    const undefinedOption = {
                        label: undefinedPlaceholderLabel,
                        value: undefinedPlaceholderValue,
                    };
                    xAxisOptions.push(undefinedOption);
                }
                // now that we have the correct full set of options (with null/undefined if needed)...
                // make array of xAxisObjects, adding fullLabel for display purposes, defaulting to empty Array if no xFieldDef for some reason...
                const xAxisObjects = xAxisOptions.map(opt => ({ ...opt, fullLabel: `${xFieldDef.label}: ${opt.label}` }));  // add fullLabel so you can display it in title of clicked selections
                // TODO - add a 'Not Set' object to xAxisObjects?  If data has any null/undefined values... need to add it here
                // xAxisValues - the 'values' of the field we're showing in X-axis (if any) - not the labels to display, but used to get things from items
                const xAxisValues = xAxisObjects.map(xo => xo.value);  // will include our null/undefined placeholders IF NEEDED
                const xAxisValuesStrings = xAxisValues.map(xv => String(xv));
                // xAxisLabels - the labels to show on the x-axis, if any
                const xAxisLabels = xAxisObjects.map(xo => xo.label);  // will include our null/undefined placeholders IF NEEDED
                // now make a dataset from the series -- and build up the actual data array from datasetCountMaps above
                const datasets = [];
                this.seriesInstances.forEach((ser, ind) => {
                    const countMap = datasetCountMaps[ind];  // use index to get the countMap we used above so we don't have to redo countBy for all data!
                    const countsArray = xAxisValuesStrings.map(val => {  // use values coerced to strings here, because your count maps should have things keyed by strings
                        return countMap[val] ?? 0;  // if no counts for that bin, default to zero
                    });
                    datasets.push(ser.makeDatasetWithData(countsArray));
                });
                // done making datasets, now have to combine them with labels (must be array with same length as datasets.data!)
                this.chartData = {
                    labels: xAxisLabels,
                    datasets: datasets,
                };
                this.datapointDefs = [...xAxisObjects];
                return;
                /* const binnedSplitData = {
                    labels: xAxisLabels,
                    datasets: this.seriesInstances.map(ser => ser.makeDataset(this.localXKey, xAxisValues)),
                };
                this.datapointDefs = [ ...xAxisObjects ];
                this.chartData = binnedSplitData;
                return; */
            } else {
                // if we DO NOT have xAxis, then we'll show a pie chart or something
                // overall labels will be the series labels, not xAxis labels
                // and we will have ONE dataset, with data and background color as arrays pulled from allSeries
                const splitData = {
                    labels: this.seriesInstances.map(ser => ser.label),
                    datasets: [
                        {
                            data: this.seriesInstances.map(ser => ser.getCounts()),  // nothing to pass to getCounts -- no 'bins' on x-axis, so just getting count of all items in the series
                            backgroundColor: this.seriesInstances.map(ser => ser.backgroundColor),
                        }
                    ],
                };
                this.datapointDefs = [ ...this.seriesInstances ];
                this.chartData = splitData;
                return;
            }
        },
        updateAll() {
            // DO NOT setLocalData here -- in case you changed things in config, only do that on first created/mounted
            // giving all computed stuff a chance to update first...
            console.log('hello from updateAll');
            this.$nextTick(() => {
                this.buildSeries();
                this.setChartDataFromSeries();
                this.setChartOptions();
            });
        },
        updateData() {
            // DO NOT setLocalData here -- in case you changed things in config, only do that on first created/mounted
            // call this whene only the data is update, no bins updated
            // otherwise it will re-render all graphs twice -- once for setChartData() and once for setChartOptions()
            // giving all computed stuff a chance to update first...
            this.$nextTick(() => {
                this.buildSeries();
                this.setChartDataFromSeries();
            });
        },
        setChartOptions() {
            // call this after knowing what chart type is (from chartConfig)
            // we DO NOT re-run this if only this.items is changed -- otherwise it will refresh the chart twice
            const alwaysShowLegendTypes = ['pie', 'doughnut'];  // even if we don't have multiple bins, so for these - but for 'bar', etc. only show if you have multiple bins, otherwise data is simple enough for no legend
            const chartOptions = {
                responsive: true,
                maintainAspectRatio: false,  // default is true, need false so it adjusts to the height you set
                hoverMode: 'index',
                //stacked: false,
                plugins: {
                    legend: {
                        display: (this.hasXAxis || alwaysShowLegendTypes.includes(this.chartConfig.chartType)),
                        position: this.hasXAxis ? 'top' : 'left',
                    }
                }
            };
            if (this.finalTitle) {
                const titleOpts = {
                    text: this.finalTitle,
                    display: true,
                    position: 'top',
                    font: {
                        size: 18,
                    }
                    // lots more options possible
                };
                set(chartOptions, 'plugins.title', titleOpts);
            }
            if (this.finalYAxisTitle) {
                const yAxisTitleOpts = {
                    text: this.finalYAxisTitle,
                    display: true,
                    // lots more options
                }
                set(chartOptions, 'scales.y.title', yAxisTitleOpts);
            }
            // const stackedTypes = ['bar', 'line'];  // not basing it on chartType anymore... added field in chartConfig
            const isStacked = this.chartConfig.isStacked;
            if (isStacked) {
                set(chartOptions, 'scales.x.stacked', true);
                set(chartOptions, 'scales.y.stacked', true);
            }
            this.chartOptions = chartOptions;
        },
        handleChartSelect(payload) {  // payload will be { dataset, element, originalEvent }
            // dataset in payload is just the data array (with some extra stuff) but NOT our dataset object with the added key - so we have to lookup the dataset ourselves from our component instance data
            //console.log('Chart select event', payload);
            const { datasetIndex, index } = payload.element;  // index is index of datapoint in that dataset
            const ourDataset = this.chartData.datasets[datasetIndex]; 
            const datasetKey = ourDataset.key;
            const datasetLabel = ourDataset.fullLabel;  // might not have it if only one dataset
            const datapointDef = this.datapointDefs[index];
            //console.log('datasetKey', datasetKey);
            //console.log('datapointDef', datapointDef);
            let splitKeyValue = null;  // defaults, then set below based on what type of data you have
            let xAxisValue = null;
            let selectionTitle = '';  // we'll make a descriptive title to pass up about what this selection represents
            if (this.hasXAxis) {
                splitKeyValue = datasetKey; 
                xAxisValue = datapointDef.value;  // datapointDef will be options object { value, label }
                selectionTitle = [datasetLabel, datapointDef.fullLabel].filter(label => !!label).join(' - ');  // remove the empty ones, then join with simple dash for now
            } else {  // if no bins (pie chart) then datasetKey will be undefined?  and datapointDef will be a single series (split using splitKey)
                splitKeyValue = datapointDef.key;  // get series key
                xAxisValue = null;
                selectionTitle = [datasetLabel, datapointDef.fullLabel].filter(label => !!label).join(' - ');  // remove the empty ones, then join with simple dash for now
            }
            const clickedItems = this.getItems(splitKeyValue, xAxisValue);
            //console.log(`CLICKED ON ${clickedItems.length} ITEMS`);
            //console.log(clickedItems);
            // EMIT SOMETHING TO PARENT - just list of IDs for the clickedItems?  Maybe better, so you don't accidentally mutate things... parent will have them already
            const clickedItemIds = clickedItems.map(item => item.id);
            const showDetailParams = {
                itemIds: clickedItemIds,
                title: selectionTitle,
            };
            this.$emit('show-detail', showDetailParams);
        },
        getItems(splitKeyValue, xAxisValue) {
            // helper to get items from this.items that meets specific criteria
            // likely used from click handler to get items in something we clicked on
            // splitKeyValue is the VALUE of the split series we clicked on - will be null if we didn't use splitKey
            // xAxisValue is the xAxisValue for the x-axis bin we clicked on, if bins are used - will be null if we didn't use xAxis / bins or want all?
            // build up the filters if you have each of these things, then filter
            //console.log('getItems', splitKeyValue, xAxisValue);
            const filters = [];
            if (splitKeyValue) {
                // const splitKeyFilter = (item) => (get(item, this.splitKey, undefinedPlaceholderValue) == splitKeyValue);
                const splitKeyFilter = (item) => (getItemValueOrPlaceholder(item, this.splitKey) == splitKeyValue);  // using new helper method
                filters.push(splitKeyFilter);
                //console.log(splitKeyFilter);
            }
            if (xAxisValue) { // xAxisValue should be a string or number here?  pay attention
                // using == instead of === so we match strings/numbers
                // ALSO... will your x-axis stuff have undefined placeholder values???  must check...
                //const xAxisFilter = (item) => (get(item, this.localXKey, undefinedPlaceholderValue) == xAxisValue);
                const xAxisFilter = (item) => (getItemValueOrPlaceholder(item, this.localXKey) == xAxisValue);  // using new helper method
                filters.push(xAxisFilter);
            }
            // now run the filters
            return this.items.filter(item => {
                return filters.every(cond => cond(item));
            });
        },
        handleChartLoaded(chartInstance) {
            // when chart emits 'loaded' it is ready - the payload is the ChartJS chart instance
            //console.log('chart loaded', chartInstance);
            this.chartInstance = chartInstance;
        },
        getChartBase64Image() {
            const chartInst = this.chartInstance;
            return chartInst ? chartInst.toBase64Image() : '';  // we don't have this.chartInstance until it first @loaded event
            // then use as href on some achor, with download="filename.png" or something
        },
        downloadChartImage() {
            //console.log(payload);
            var chartLink = document.createElement('a');
            chartLink.href = this.getChartBase64Image();  // will be a PNG unless otherwise specified
            chartLink.download = 'chart_image.png';
            chartLink.click();
        },
    }
}
</script>

<style>

</style>